import React, { useEffect, useRef, useState } from "react";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import { Paperclip, FileUp, Trash2, Download, Eye, FileText, Image as ImageIcon, FileSpreadsheet, Loader2, X } from "lucide-react";
import { fmtDate } from "../lib/format";

const iconFor = (ct) => {
    if (!ct) return FileText;
    if (ct.startsWith("image/")) return ImageIcon;
    if (ct.includes("pdf")) return FileText;
    if (ct.includes("sheet") || ct.includes("csv") || ct.includes("excel")) return FileSpreadsheet;
    return FileText;
};

const fmtSize = (n) => {
    if (n < 1024) return `${n} B`;
    if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1048576).toFixed(2)} MB`;
};

/**
 * Reusable Attachment Panel — file uploads for any entity.
 *
 * Props:
 *   entityType - e.g. "voucher", "purchase_order", "grn", "quotation", ...
 *   entityId   - id of the specific document (e.g. voucher id)
 *   title      - optional panel title (default: "Attachments")
 *   compact    - if true, renders as a small inline chip-list (used in row modals)
 */
export default function AttachmentPanel({ entityType, entityId, title = "Attachments", compact = false, uploadedBy = "" }) {
    const [files, setFiles] = useState([]);
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);
    const inputRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);

    const refresh = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/files/?entity_type=${entityType}&entity_id=${encodeURIComponent(entityId)}`);
            setFiles(r.data.attachments);
        } catch (e) {
            console.error(e);
        } finally { setLoading(false); }
    };

    useEffect(() => { if (entityId) refresh(); }, [entityId, entityType]);

    const upload = async (fileList) => {
        if (!fileList?.length) return;
        setBusy(true);
        let ok = 0, fail = 0;
        for (const file of fileList) {
            if (file.size > 25 * 1024 * 1024) {
                toast.error(`${file.name} exceeds 25 MB — skipped`);
                fail++;
                continue;
            }
            const fd = new FormData();
            fd.append("file", file);
            fd.append("entity_type", entityType);
            fd.append("entity_id", entityId);
            fd.append("uploaded_by", uploadedBy || "");
            try {
                const token = localStorage.getItem("awc_token");
                const res = await fetch(`${API}/files/upload`, {
                    method: "POST",
                    body: fd,
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                if (!res.ok) {
                    const err = await res.json();
                    throw new Error(err.detail || `HTTP ${res.status}`);
                }
                ok++;
            } catch (e) {
                console.error(e);
                toast.error(`${file.name}: ${e.message}`);
                fail++;
            }
        }
        setBusy(false);
        if (ok) toast.success(`${ok} file${ok > 1 ? "s" : ""} attached · Persisted to cloud storage`);
        refresh();
    };

    const del = async (id) => {
        if (!confirm("Delete this attachment?")) return;
        try {
            await api.delete(`/files/${id}`);
            toast.success("Attachment deleted");
            refresh();
        } catch (e) { toast.error("Delete failed"); }
    };

    const download = (f) => {
        const token = localStorage.getItem("awc_token");
        const url = `${API}/files/${f.id}/download${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
        window.open(url, "_blank");
    };

    const preview = (f) => {
        const token = localStorage.getItem("awc_token");
        const url = `${API}/files/${f.id}/preview${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
        window.open(url, "_blank");
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        upload(Array.from(e.dataTransfer.files || []));
    };

    return (
        <div className={compact ? "" : "border border-panel p-3 bg-[var(--bg-panel-hover)]"}>
            {!compact && (
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <Paperclip size={13} className="text-[var(--brand-amber)]" />
                        <div className="text-[10px] font-mono-fin uppercase tracking-[0.2em] text-[var(--brand-amber)]">{title}</div>
                        <div className="text-[9px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)]">
                            {files.length} file{files.length !== 1 ? "s" : ""}
                        </div>
                    </div>
                </div>
            )}

            <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                className={`border border-dashed p-3 mb-2 text-center transition-colors ${dragOver ? "border-[var(--brand-amber)] bg-[var(--brand-amber-dim)]" : "border-panel"}`}
            >
                <input
                    ref={inputRef}
                    type="file"
                    multiple
                    className="hidden"
                    data-testid={`att-input-${entityType}-${entityId}`}
                    onChange={(e) => upload(Array.from(e.target.files || []))}
                />
                <div className="flex items-center justify-center gap-2 flex-wrap">
                    <button
                        onClick={() => inputRef.current?.click()}
                        disabled={busy}
                        data-testid={`att-upload-btn-${entityType}-${entityId}`}
                        className="px-3 py-1.5 border border-[var(--brand-amber)] text-[var(--brand-amber)] font-mono-fin text-[10px] uppercase tracking-[0.15em] hover:bg-[var(--brand-amber)] hover:text-black disabled:opacity-40 flex items-center gap-1.5"
                    >
                        {busy ? <Loader2 size={11} className="animate-spin" /> : <FileUp size={11} />}
                        {busy ? "Uploading..." : "Upload File(s)"}
                    </button>
                    <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)]">
                        or drag & drop · Max 25 MB · PDF / Image / Doc / Excel / CSV
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="text-[10px] font-mono-fin text-[var(--text-muted)] text-center py-2 uppercase tracking-wider">Loading...</div>
            ) : files.length === 0 ? (
                <div className="text-[10px] font-mono-fin text-[var(--text-muted)] text-center py-2 uppercase tracking-wider">No attachments yet</div>
            ) : (
                <div className="space-y-1">
                    {files.map((f) => {
                        const Icon = iconFor(f.content_type);
                        return (
                            <div key={f.id} data-testid={`att-row-${f.id}`} className="flex items-center gap-2 border border-panel p-2 hover:bg-[var(--bg-panel-hover)] group">
                                <Icon size={14} className="text-[var(--brand-amber)] shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs text-[var(--text-primary)] truncate font-body">{f.original_filename}</div>
                                    <div className="text-[9px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)]">
                                        {fmtSize(f.size)} · {fmtDate(f.created_at)}{f.uploaded_by && ` · ${f.uploaded_by}`}
                                    </div>
                                </div>
                                <button data-testid={`att-preview-${f.id}`} onClick={() => preview(f)} className="text-[var(--text-muted)] hover:text-[var(--brand-amber)] px-1" title="Preview inline"><Eye size={12} /></button>
                                <button data-testid={`att-download-${f.id}`} onClick={() => download(f)} className="text-[var(--text-muted)] hover:text-[var(--brand-amber)] px-1" title="Download"><Download size={12} /></button>
                                <button data-testid={`att-delete-${f.id}`} onClick={() => del(f.id)} className="text-[var(--text-muted)] hover:text-neg px-1" title="Delete"><Trash2 size={12} /></button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}


/**
 * Attachment button with a modal — for use in dense tables where inline panel is too big.
 */
export function AttachmentButton({ entityType, entityId, uploadedBy = "", label = "" }) {
    const [open, setOpen] = useState(false);
    const [count, setCount] = useState(0);

    useEffect(() => {
        if (entityId) {
            api.get(`/files/?entity_type=${entityType}&entity_id=${encodeURIComponent(entityId)}`)
                .then((r) => setCount(r.data.summary.count))
                .catch(() => {});
        }
    }, [entityId, entityType, open]);

    return (
        <>
            <button
                data-testid={`att-btn-${entityType}-${entityId}`}
                onClick={(e) => { e.stopPropagation(); setOpen(true); }}
                className="text-[var(--text-muted)] hover:text-[var(--brand-amber)] flex items-center gap-1 text-[10px] font-mono-fin uppercase tracking-wider"
                title="Attachments"
            >
                <Paperclip size={12} />
                {count > 0 && <span className="text-[var(--brand-amber)]">{count}</span>}
                {label}
            </button>

            {open && (
                <div className="fixed inset-0 z-40 bg-[var(--bg-base)]/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
                    <div className="w-full max-w-2xl bg-panel border border-[var(--brand-amber)]" onClick={(e) => e.stopPropagation()}>
                        <div className="border-b border-panel px-4 py-3 flex items-center justify-between">
                            <div>
                                <div className="text-xs font-mono-fin uppercase tracking-[0.15em] text-[var(--brand-amber)]">Attachments</div>
                                <div className="text-[10px] font-mono-fin text-[var(--text-secondary)] uppercase tracking-wider">{entityType} · {entityId}</div>
                            </div>
                            <button onClick={() => setOpen(false)} data-testid="att-modal-close" className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"><X size={14} /></button>
                        </div>
                        <div className="p-4">
                            <AttachmentPanel entityType={entityType} entityId={entityId} uploadedBy={uploadedBy} />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
